import { Controller } from "@hotwired/stimulus"

// How long "Pagar" may wait for an answer: Rails answers the POST in about a second (the
// gateway is called from a job).
const PAYMENT_STALL_TIMEOUT_MS = 15000

// Hide checkout line-item remove controls while payment is submitting / loading.
//
// Also a watchdog on "Pagar": an iPhone stayed on "Procesando pago…" while no request reached
// Rails. When the pay request gets no answer in time, stop it, give the button back, show
// stallNotice, report it to stallReportUrl (the only trace when nothing reaches Rails) and
// leave the next try to a plain browser POST instead of Turbo's fetch.
export default class extends Controller {
  static targets = ["removableAction", "stallNotice"]
  static values = { stallReportUrl: String }

  connect() {
    this.form = this.element.querySelector("#checkout-payment-form")
    if (!this.form) return

    this.onClickBound = this.onPayClick.bind(this)
    this.onSubmitBound = this.onSubmit.bind(this)
    this.onSubmitStartBound = this.onSubmitStart.bind(this)
    this.onSubmitEndBound = this.onSubmitEnd.bind(this)

    this.form.addEventListener("click", this.onClickBound)
    this.form.addEventListener("submit", this.onSubmitBound)
    this.form.addEventListener("turbo:submit-start", this.onSubmitStartBound)
    this.form.addEventListener("turbo:submit-end", this.onSubmitEndBound)
  }

  disconnect() {
    this.disarmWatchdog()
    if (!this.form) return

    this.form.removeEventListener("click", this.onClickBound)
    this.form.removeEventListener("submit", this.onSubmitBound)
    this.form.removeEventListener("turbo:submit-start", this.onSubmitStartBound)
    this.form.removeEventListener("turbo:submit-end", this.onSubmitEndBound)
  }

  // Align with button_controller#setLoading: hide as soon as pay click shows loading UI.
  onPayClick(event) {
    const submitter = event.target.closest("button, input[type='submit']")
    if (!submitter) return
    if (submitter.type === "button" || submitter.type === "reset") return
    if (!this.form.contains(submitter) && submitter.getAttribute("form") !== this.form.id) return
    if (!this.form.checkValidity()) return

    this.lock()
    this.armWatchdog(submitter)
  }

  onSubmit(event) {
    if (!this.form.checkValidity()) {
      // Match button_controller: restore if HTML5 validation blocked submit.
      setTimeout(() => this.unlock(), 0)
      return
    }

    this.lock()
    this.armWatchdog(event.submitter)
  }

  onSubmitStart(event) {
    this.formSubmission = event.detail.formSubmission
    this.lock()
    this.armWatchdog()
  }

  onSubmitEnd() {
    this.formSubmission = null
    this.disarmWatchdog()
    this.unlock()
  }

  lock() {
    this.removableActionTargets.forEach((el) => {
      el.classList.add("hidden")
      el.disabled = true
      el.setAttribute("aria-hidden", "true")
    })
  }

  unlock() {
    this.removableActionTargets.forEach((el) => {
      el.classList.remove("hidden")
      el.disabled = false
      el.removeAttribute("aria-hidden")
    })
  }

  armWatchdog(submitter) {
    if (submitter) this.submitter = submitter
    if (this.watchdog) return

    this.payStartedAt = Date.now()
    if (this.hasStallNoticeTarget) this.stallNoticeTarget.hidden = true
    this.watchdog = setTimeout(() => this.payStalled(), PAYMENT_STALL_TIMEOUT_MS)
  }

  disarmWatchdog() {
    clearTimeout(this.watchdog)
    this.watchdog = null
  }

  payStalled() {
    this.watchdog = null
    const submissionStarted = Boolean(this.formSubmission)
    this.reportStall(submissionStarted)
    this.form.dataset.turbo = "false"

    if (submissionStarted) {
      // Aborts the fetch; Turbo then fires turbo:submit-end, which gives the button back.
      this.formSubmission.stop()
    } else {
      this.payButtonController()?.resetLoading()
      this.unlock()
    }

    if (this.hasStallNoticeTarget) this.stallNoticeTarget.hidden = false
  }

  payButtonController() {
    const button = this.submitter || this.form.querySelector("[data-controller~='button'][type='submit']")
    if (!button) return null

    return this.application.getControllerForElementAndIdentifier(button, "button")
  }

  reportStall(submissionStarted) {
    if (!this.hasStallReportUrlValue || !navigator.sendBeacon) return

    const body = new FormData()
    const tokenParam = document.querySelector("meta[name='csrf-param']")?.content
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (tokenParam && token) body.append(tokenParam, token)
    body.append("submission_started", submissionStarted)
    body.append("elapsed_ms", Date.now() - this.payStartedAt)
    body.append("online", navigator.onLine)

    try {
      navigator.sendBeacon(this.stallReportUrlValue, body)
    } catch (_error) {
      // Reporting must never get in the way of paying.
    }
  }
}
